import { trigger, transition, style, animate } from '@angular/animations';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { SharedService } from '@shared/services/shared.service';
import { IEntityLicense } from 'app/models/licenses/entity-license';
import { EntityLicenseService } from 'app/services/license/entity-license.service';
import { EntityApplicantComponent } from '../entity-applicant/entity-applicant.component';
import { IndividualApplicantComponent } from '../individual-applicant/individual-applicant.component';
import { DocumentComponent } from '../document/document.component';
import { AddressComponent } from '../address/address.component';
import { Router } from '@angular/router';
import { ISearch } from 'app/models/licenses/search';
import { IStaff } from 'app/models/user/staff';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'app-entity-license',
  templateUrl: './entity-license.component.html',
  styleUrls: ['./entity-license.component.scss'],
  animations: [
    trigger('slideLeft', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
      ])
    ]),
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})
export class EntityLicenseComponent implements OnInit {
  @ViewChild(EntityApplicantComponent) entityApplicants: EntityApplicantComponent;
  @ViewChild(IndividualApplicantComponent) individualApplicants: IndividualApplicantComponent;
  @ViewChild(DocumentComponent) documents: DocumentComponent;
  @ViewChild(AddressComponent) addresses: AddressComponent;

  public search: ISearch = {
    entityName: '',
    firstName: '',
    lastName: '',
    licenseId: '',
    statuses: [],
    assignedTo: ''
  }
  public searchForm = new FormGroup({
    entityName: new FormControl(''),
    licenseId: new FormControl(''),
    assignedTo: new FormControl(''),
    statuses: new FormControl<string[] | null>(['Submitted'])
  });

  public licenseStaff: IStaff[] = [];

  public step: number = 1;
  public viewingEntityApplicant: boolean = false;
  public viewingIndividualApplicant: boolean = false;
  public viewingApplicant: boolean = false;
  public license: IEntityLicense = {
    id: 0,
    userId: '',
    licenseId: '',
    entityName: '',
    entityWebsite: '',
    primaryContactName: '',
    primaryContactNumber: '',
    primaryContactEmail: '',
    businessInOregon: false,
    secretaryOfStateNumber: '',
    registeredBusiness: false,
    registeredIn: '',
    registryNumber: '',
    enteredByStaff: false,
    assignedToId: '',
    assignedToName: '',
    finalApprovalBy: '',
    canRenew: false,
    isRenewal: false,
    electronicSignature: '',
    applicationDate: '',
    approvedDate: '',
    deniedDate: '',
    expirationDate: '',
    statuses: []
  }
  public licenses: IEntityLicense[] = [];
  public licenseForm = new FormGroup({
    entityName: new FormControl('', Validators.required),
    entityWebsite: new FormControl('', Validators.pattern( /^(http:\/\/|https:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]/i )),
    primaryContactName: new FormControl('', Validators.required),
    primaryContactNumber: new FormControl('', Validators.required),
    primaryContactEmail: new FormControl('', [Validators.required, Validators.email]),
    businessInOregon: new FormControl(false),
    secretaryOfStateNumber: new FormControl(''),
    registeredBusiness: new FormControl(false),
    registeredIn: new FormControl(''),
    registryNumber: new FormControl('')
  });

  public licenseColumns: string [] = ['licenseId', 'entityName', 'primaryContactName', 'statuses', 'assign'];
  public licenseSource = new MatTableDataSource<IEntityLicense>(this.licenses);
  @ViewChild('licensePaginator', {static: false}) licensePaginator: MatPaginator;
  @ViewChild('licenseSort', {static: false}) licenseSort: MatSort;

  public sharedService = inject(SharedService);
  public licenseService = inject(EntityLicenseService);
  public router = inject(Router);

  ngOnInit(): void {
    this.searchLicenses();
    this.getStaff();
  }

  searchLicenses(): void {
    let form = this.searchForm.value;
    this.search.entityName = form.entityName;
    this.search.licenseId = form.licenseId;
    this.search.statuses = form.statuses;
    if(this.search.statuses[0] === null){
      this.search.statuses = [];
    }
    this.search.assignedTo = form.assignedTo;

    this.licenseService.searchLicenses(this.search).subscribe({
      next: response => this.licenses = response,
      error: e => {
        this.sharedService.toastr.error("Could not retrieve licenses");
        console.log('error searching licenses', e);
      },
      complete: () => this.updateLicenseTable()
    });
  }

  getStaff(): void{
    this.licenseService.getStaff().subscribe({
      next: response => this.licenseStaff = response,
      error: e => console.log('error retrieving staff', e)
    });
  }

  updateLicenseTable(): void {
    setTimeout(() => {
      this.licenseSource.data = this.licenses;
      this.licenseSource.sort = this.licenseSort;
      this.licenseSource.paginator = this.licensePaginator;
    }, 500);
  }

  filterTable(event: Event): void {
    let value = (event.target as HTMLInputElement).value;
    let filter = value.trim().toLocaleLowerCase();
    this.licenseSource.filter = filter;
  }

  assignLicense(event: MatSelectChange, licenseId: number): void {
    let user: IStaff = this.licenseStaff.find(staff => staff.name === event.value);
    if(user === undefined){
      let emptyUser: IStaff = {
        id: '',
        name: '',
        email: ''
      }
      user = emptyUser;
    }
    this.licenseService.assignLicense(licenseId, user).subscribe(
      () =>{
        this.licenses.find(tp => tp.id == licenseId).assignedToName = user.name;
        if(user === null || user.name === null || user.name.length === 0) {
          this.sharedService.toastr.success("License Unassigned");
        }
        else {
          this.sharedService.toastr.success("License assigned to " + user.name);
        }
      },
      error => console.log('error', error)
    );
  }

  createLicense(): void {
    this.sharedService.openConfirm("Create a new entity license?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.createLicense().subscribe({
            next: response => this.license = response,
            error: e => {
              console.log("Error Creating License", e);
              this.sharedService.toastr.error("Error Creating License: " + e);
            },
            complete: () => {
              this.licenses.push(this.license);
              this.sharedService.toastr.success("Created license");
            }
          });
        }
      }
    );
  }

  updateLicenseForm(license: IEntityLicense): void {
    this.licenseForm.reset({
      entityName: license.entityName,
      entityWebsite: license.entityWebsite,
      primaryContactName: license.primaryContactName,
      primaryContactNumber: license.primaryContactNumber,
      primaryContactEmail: license.primaryContactEmail,
      businessInOregon: license.businessInOregon,
      secretaryOfStateNumber: license.secretaryOfStateNumber,
      registeredBusiness: license.registeredBusiness,
      registeredIn: license.registeredIn,
      registryNumber: license.registryNumber,
    });
  }

  updateLicense(): void {
    let form = this.licenseForm.value;
    this.license.entityName = form.entityName;
    this.license.entityWebsite = form.entityWebsite;
    this.license.primaryContactName = form.primaryContactName;
    this.license.primaryContactNumber = form.primaryContactNumber;
    this.license.primaryContactEmail = form.primaryContactEmail;
    this.license.businessInOregon = form.businessInOregon;
    this.license.secretaryOfStateNumber = form.secretaryOfStateNumber;
    this.license.registeredBusiness = form.registeredBusiness;
    this.license.registeredIn = form.registeredIn;
    this.license.registryNumber = form.registryNumber;
  }

  getEmptyLicense(): IEntityLicense {
    return {
      id: 0,
      userId: '',
      licenseId: '',
      entityName: '',
      entityWebsite: '',
      primaryContactName: '',
      primaryContactNumber: '',
      primaryContactEmail: '',
      businessInOregon: false,
      secretaryOfStateNumber: '',
      registeredBusiness: false,
      registeredIn: '',
      registryNumber: '',
      enteredByStaff: false,
      assignedToId: '',
      assignedToName: '',
      finalApprovalBy: '',
      canRenew: false,
      isRenewal: false,
      electronicSignature: '',
      applicationDate: '',
      approvedDate: '',
      deniedDate: '',
      expirationDate: '',
      statuses: []
    }
  }

  selectLicense(license: IEntityLicense): void {
    this.license = license;
    this.updateLicenseForm(license);
  }

  closeLicense(save: boolean): void {
    if(save){
      this.saveLicense();
    }
    this.viewingEntityApplicant = false;
    this.viewingIndividualApplicant = false;
    this.license = this.getEmptyLicense();
    this.updateLicenseForm(this.license);
  }

  saveLicense(): void {
    this.updateLicense();
    this.licenseService.updateLicense(this.license).subscribe({
      complete: () => this.sharedService.toastr.success("Entity License Saved"),
      error: e => {
        this.sharedService.toastr.error('Error Updating Entity License');
        console.log('updating entity license error', e);
      }
    });
  }

  submitLicense(approved: boolean): void {
    this.sharedService.openConfirm(approved? 'Approve Application?' : 'Deny Application?');
    this.sharedService.confirmed().subscribe(
      (response) => {
        if(response){
          this.licenseService.approveLicense(this.license.id, approved).subscribe({
            next: response => {
              let licenseIndex: number = this.licenses.findIndex(l => l.id === this.license.id);
              this.licenses[licenseIndex] = response;
              this.license = response;
            },
            error: e => {
              console.log('error', e);
              this.sharedService.toastr.error("Could Not Complete License");
            },
            complete: () => {
              this.sharedService.toastr.success("License Completed");
              this.closeLicense(false);
            }
          });
        }
      }
    );
  }

  compareFn(type1: string, type2: string): boolean {
    return type1 === type2;
  }

  public get locked(): boolean {
    return this.license.statuses.some(s => s.label === this.sharedService.submitted);
  }

  public get complete(): boolean {
    return this.license.statuses.some(s => s.label === this.sharedService.approved || s.label === this.sharedService.denied);
  }

  public get applicationValid(): boolean {
    let entityApplicantsValid: boolean = this.entityApplicants?.applicants?.length > 0;
    let individualApplicantsValid: boolean = this.individualApplicants?.applicants?.length > 0;
    let documentsValid: boolean = this.documents?.documents?.length > 0;
    let addressesValid: boolean = this.addresses?.addresses?.length > 0;
    return entityApplicantsValid && individualApplicantsValid && documentsValid && addressesValid && this.licenseForm.valid;
  }

  entityApplicantSelected(viewing: boolean):void {
    this.viewingEntityApplicant = viewing;
    this.viewingApplicant = viewing;
  }

  individualApplicantSelected(viewing: boolean):void {
    this.viewingIndividualApplicant = viewing;
    this.viewingApplicant = viewing;
  }
}
